'use client';

import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dateToSimpleDate, mondayOf, type ChallengeListing } from '@puzzlio/core';
import type { ChallengeStatus } from '@puzzlio/games';
import { challengeKey, useChallengeStatuses } from './gameBoardAndPlayHooks';
import { useStripeSubscription } from './useStripe';
import { useUser, useUserLoading } from '@/app/providers/UserProvider';
import { hasSubscriberAccess } from '@/app/auth/utils';
import { trackSubscriptionPromptShown } from '@/app/lib/analytics-events';

/** Weekly challenges are admin-only until launch: nothing outside the admin pages offers them to anyone else. */
export function useChallengesEnabled(): boolean {
  return useUser()?.role === 'admin';
}

/** Every released weekly challenge, as /api/challenges lists it. Changes about once a week. */
export function useChallengeListings({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    enabled,
    queryKey: ['challenges'],
    queryFn: async (): Promise<ChallengeListing[]> => {
      const res = await fetch('/api/challenges');
      if (!res.ok) throw new Error(`/api/challenges failed: ${res.status}`);
      return (await res.json()).challenges;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The challenges of the player's current week (Monday to Sunday where they are), with their progress on
 * each. Undefined until both the listing and the player's plays are in hand; empty in a week without any.
 */
export function useThisWeeksChallenges(): (ChallengeListing & ChallengeStatus)[] | undefined {
  const enabled = useChallengesEnabled();
  const { data: listings } = useChallengeListings({ enabled });
  const statuses = useChallengeStatuses();
  if (!enabled) return [];
  if (!listings || !statuses) return undefined;

  const thisWeek = mondayOf(dateToSimpleDate(new Date()));
  return listings
    .filter((challenge) => challenge.week === thisWeek)
    .map((challenge) => ({
      ...challenge,
      status: statuses.get(challengeKey(challenge.gameType, challenge.week)) ?? 'not-started',
    }));
}

/**
 * Whether to pitch weekly challenges to this player: true once they're known not to be a subscriber.
 * False while that's still loading, so a subscriber never sees a flash of sales pitch.
 */
export function useShowChallengePitch(): boolean {
  const [user, isUserLoading] = useUserLoading();
  const { data: subscription, isLoading: isSubscriptionLoading } = useStripeSubscription();
  return !isUserLoading && !isSubscriptionLoading && !(!!user && hasSubscriberAccess(subscription, user));
}

/**
 * Starts loading this week's challenges as a subscriber opens a puzzle, so the solved screen's ladder,
 * which waits for them, has them by the time the puzzle is finished.
 */
export function useWarmChallenges() {
  const user = useUserLoading()[0];
  const { data: subscription } = useStripeSubscription();
  const enabled = useChallengesEnabled();
  useChallengeListings({ enabled: enabled && !!user && hasSubscriberAccess(subscription, user) });
}

/**
 * Counts locked challenge cards as a subscription prompt once `pitching` turns true: each one leads to
 * subscribing, so they're the pitch wherever they're shown.
 */
export function useTrackChallengePitch(pitching: boolean) {
  useEffect(() => {
    if (pitching) trackSubscriptionPromptShown(undefined, 'challenge');
  }, [pitching]);
}
